import { supabase } from '../config/supabase';

export interface UploadResult {
  url: string | null;
  error: Error | null;
}

export const storageService = {
  // Upload business logo
  async uploadBusinessLogo(file: File, businessId: string): Promise<UploadResult> {
    try {
      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;
      const filePath = `business-logos/${businessId}/${fileName}`;

      // Upload the file
      const { data, error: uploadError } = await supabase.storage
        .from('business-logos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('business-logos')
        .getPublicUrl(filePath);

      return { url: urlData.publicUrl, error: null };
    } catch (error) {
      return { url: null, error: error as Error };
    }
  },

  // Delete business logo
  async deleteBusinessLogo(logoUrl: string): Promise<{ error: Error | null }> {
    try {
      // Extract the file path from the URL
      const url = new URL(logoUrl);
      const pathParts = url.pathname.split('/');
      const bucketIndex = pathParts.findIndex(part => part === 'business-logos');
      const filePath = pathParts.slice(bucketIndex).join('/');

      const { error } = await supabase.storage
        .from('business-logos')
        .remove([filePath]);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Upload purchase order attachment
  async uploadPurchaseOrderAttachment(file: File, businessId: string, poNumber?: string): Promise<UploadResult> {
    try {
      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const timestamp = Date.now();
      const fileName = poNumber
        ? `${poNumber}-${timestamp}.${fileExt}`
        : `attachment-${timestamp}.${fileExt}`;
      const filePath = `${businessId}/${fileName}`;

      // Upload the file
      const { data, error: uploadError } = await supabase.storage
        .from('purchase-order-attachments')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('purchase-order-attachments')
        .getPublicUrl(filePath);

      return { url: urlData.publicUrl, error: null };
    } catch (error) {
      return { url: null, error: error as Error };
    }
  },

  // Delete purchase order attachment
  async deletePurchaseOrderAttachment(attachmentUrl: string): Promise<{ error: Error | null }> {
    try {
      // Extract the file path from the URL
      const url = new URL(attachmentUrl);
      const pathParts = url.pathname.split('/');
      const bucketIndex = pathParts.findIndex(part => part === 'purchase-order-attachments');
      const filePath = pathParts.slice(bucketIndex).join('/');

      const { error } = await supabase.storage
        .from('purchase-order-attachments')
        .remove([filePath]);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Get public URL for a file
  getPublicUrl(bucket: string, path: string): string {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);

    return data.publicUrl;
  }
};

// Export empty object to make this a proper module
export {};